import React from 'react';
import { Button } from './ui/button';
import { Shield, Crown, AlertCircle } from 'lucide-react';
import { useAuth } from '../lib/auth';

/**
 * FeatureGuard Component
 * Handles Maintenance and VIP restrictions for features.
 * 
 * @param {Object} props
 * @param {Object} props.feature - The feature flag object from backend
 * @param {React.ReactNode} props.children - Content to show if allowed
 */
export default function FeatureGuard({ feature, children }) {
  const { user, isAdmin } = useAuth();
  
  if (!feature) return children;

  // Admin bypasses all checks
  if (isAdmin) return children;

  // 1. Maintenance Check (Highest Priority)
  if (!feature.is_enabled) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-6 bg-white rounded-2xl border shadow-sm animate-fadeIn">
        <div className="p-4 bg-blue-50 rounded-full mb-4">
          <Shield size={48} className="text-[#1877F2]" />
        </div>
        <h2 className="text-xl font-bold text-[#1C1E21] mb-2">{feature.name} đang tạm nghỉ</h2>
        <p className="text-[#606770] max-w-md">
          {feature.message || "Tính năng này đang được bảo trì để nâng cấp nội dung. Vui lòng quay lại sau."}
        </p>
        <Button 
          className="mt-6 bg-[#1877F2] hover:bg-[#166FE5]"
          onClick={() => window.history.back()}
        >
          Quay lại Dashboard
        </Button>
      </div>
    );
  }

  // 2. VIP Check
  const isUserVIP = user?.subscription_type === 'monthly' && 
                    user?.subscription_expiry && 
                    new Date(user.subscription_expiry) > new Date();

  if (feature.is_vip && !isUserVIP) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-6 bg-white rounded-2xl border shadow-sm animate-fadeIn">
        <div className="p-4 bg-amber-50 rounded-full mb-4">
          <Crown size={48} className="text-amber-500" />
        </div>
        <h2 className="text-xl font-bold text-[#1C1E21] mb-2">{feature.name} dành cho VIP</h2>
        <p className="text-[#606770] max-w-md">
          {feature.vip_message || "Tính năng này chỉ dành riêng cho thành viên VIP. Vui lòng nâng cấp tài khoản để trải nghiệm toàn bộ tính năng cao cấp của hệ thống."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Button 
            className="bg-amber-500 hover:bg-amber-600 border-none text-white font-bold"
            onClick={() => window.location.href = '/billing'}
          >
            Nâng cấp VIP ngay
          </Button>
          <Button 
            variant="outline"
            onClick={() => window.history.back()}
          >
            Quay lại
          </Button>
        </div>
      </div>
    );
  }

  // All checks passed
  return children;
}
